import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { Button, Spin, Progress, Modal } from "antd";
import {
  PauseOutlined,
  RedoOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import environment from "../../environments";
import "./styles.scss";
import VideoRecorder from "./videorecorder";
import CountdownTimer from "./CountdownTimer";
import { useProcess } from "../../contexts/ProcessContext";
import { updateAnswer } from "../../services/interviews";

const S3URL = environment.s3Url;

function blobToBase64(blob) {
  return new Promise((resolve, _) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });
}

const Recorder = (props) => {
  const { processId, updateState } = useProcess();
  const { t } = useTranslation();

  const history = useHistory();
  const [recording, setRecording] = useState(false);
  const [trialsRemaining, setTrialsRemaining] = useState(props.trials);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [countdownFinished, setCountdownFinished] = useState(false);
  const [recorderReady, setRecorderReady] = useState(false);
  const [file, setFile] = useState({
    url: null,
    blob: null,
    mimeType: null,
  });
  const recorder = useRef({
    setUpVideo: () => {},
    startRecording: () => {},
    stopRecording: () => {},
    download: () => {},
    playRecordedVideo: () => {},
    playVideo: () => {},
  });

  const onCountdown = (time) => {
    if (time <= 0) {
      setCountdownFinished(true);
    }
  };

  const onRecordingProgress = (time) => {
    const userTime = time;
    setProgress(userTime);
    if (userTime >= props.time) {
      setRecording(false);
    }
  };

  const handleOnRecording = () => {
    setRecording(true);
  };

  const handleRecorderReady = (r) => {
    setRecorderReady(r);
  };

  // Enviar respuesta con video null si cierra pestaña grabando
  useEffect(() => {
    const handleBeforeUnload = () => {
      const token = localStorage.getItem("token");
      const answerId = props.currentQuestion.answers[0]?.id;
      if (!props.demo) {
        let data = {};
        if (props.flashInterview) {
          data.state = "INCOMPLETE";
        } else {
          // Obtener la respuesta actual
          const currentAnswer = props.currentQuestion.answers[0];
          const currentTries = currentAnswer?.tries || 0;
          const maxAttempts = props.trials;

          // Si el próximo valor de tries será >= maxAttempts, marcar como INCOMPLETE
          if (currentTries >= maxAttempts) {
            data.state = "INCOMPLETE";
          } else {
            // Si no era el último try, mantener NOT_STARTED y sumar un try
            data.state = "NOT_STARTED";
            data.tries = currentTries + 1;
          }
        }
        fetch(`${environment.apiUrl}/answers/${answerId}`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Token ${token}`,
          },
          body: JSON.stringify({ answer: data }),
          keepalive: true,
        });
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [
    recording,
    props.demo,
    props.candidateId,
    props.questionId,
    props.currentQuestion,
    props.trials,
    props.flashInterview,
  ]);

  useEffect(() => {
    if (
      props.flashInterview &&
      recorderReady &&
      !recording &&
      !countdownFinished &&
      !file.url
    ) {
      setTimeout(() => {
        setProgress(0);
        setRecording(true);
        recorder.current.startRecordingWithCountdown(3);
      }, 500);
    }
  }, [
    props.flashInterview,
    recorderReady,
    recording,
    countdownFinished,
    file.url,
  ]);

  const onFinishRecording = (url, blob, mimeType) => {
    setRecording(false);
    setCountdownFinished(true);
    setFile({
      url,
      blob,
      mimeType,
    });
    if (trialsRemaining === props.trials) {
      setTrialsRemaining((t) => t - 1);
    }
  };

  const handleCancel = () => {
    history.push(`/processes/${processId}`);
  };

  const handleStart = () => {
    history.push(`/processes/${processId}/interview`, {
      allowed: true,
    });
  };

  const handleUpload = async () => {
    setLoading(true);
    const fileBase64 = await blobToBase64(file.blob);
    const body = {
      questionId: props.questionId,
      interviewId: props.interviewId,
      fileName: file.url.split("/").pop(),
      mimeType: file.blob.type,
      candidateId: props.candidateId,
    };
    const lambdaEndpoint = environment.lambdaUrl;
    try {
      const response = await axios.post(lambdaEndpoint, body, {
        headers: { "Content-Type": "application/json" },
      });
      const videoUploadS3Url = response.data.result;

      const tags = `interview_id=${props.interviewId}&question_id=${props.questionId}&candidate_id=${props.candidateId}`;

      await axios.put(videoUploadS3Url, file.blob, {
        headers: {
          "Content-Type": file.blob.type,
          "x-amz-tagging": tags,
        },
      });
      const videoUrl = videoUploadS3Url.split("?")[0];
      const videoData = await props.uploadVideo({
        url: videoUrl,
      });

      await props.handleVideoRecorded(videoData.data.id);
      setLoading(false);
    } catch (e) {
      setLoading(false);
      Modal.error({
        centered: true,
        title: t("frontvint__upload_error_title"),
        content: t("frontvint__upload_error_content"),
        zIndex: 9999,
      });
    }
  };

  const handleRecordStop = () => {
    if (recording) {
      recorder.current.stopRecording();
      setRecording(false);
    } else {
      setProgress(0);
      setRecording(true);
      recorder.current.startRecordingWithCountdown(3);
    }
  };

  const handleReRecord = () => {
    if (trialsRemaining > 0) {
      const doReRecord = () => {
        setFile({
          url: null,
          blob: null,
          mimeType: null,
        });

        recorder.current.reset();
        setRecording(false);
        setProgress(0);
        setCountdownFinished(false);

        updateAnswer(
          {
            tries: props.currentQuestion.answers[0]?.tries + 1 || 1,
          },
          props.currentQuestion.answers[0]?.id
        )
          .then((_) => {
            updateState({
              questions: props.questions.map((q) =>
                q.id === props.currentQuestion.id
                  ? {
                      ...q,
                      answers: q.answers.map((a) =>
                        a.id === props.currentQuestion.answers[0]?.id
                          ? {
                              ...a,
                              tries:
                                (props.currentQuestion.answers[0]?.tries || 0) +
                                1,
                            }
                          : a
                      ),
                    }
                  : q
              ),
            });
            setTrialsRemaining((t) => t - 1);
          })
          .catch((err) => {
            console.error(err);
          });
      };

      if (trialsRemaining === 1 && !props.demo) {
        Modal.info({
          centered: true,
          title: t("frontvint__last_attempt_title"),
          content: t("frontvint__last_attempt_content"),
          zIndex: 9999,
          onOk: doReRecord,
        });
      } else {
        doReRecord();
      }
    }
  };

  const onPermissionSuccess = () => {
    recorder.current.setUpVideo();
  };

  const onPermissionError = () => {
    Modal.error({
      centered: true,
      title: t("frontvint__camera_mic_problem_title"),
      content: t("frontvint__camera_mic_problem_content"),
      zIndex: 9999,
    });
  };

  let date = new Date(0);
  date.setSeconds(props.time - progress);

  return (
    <div
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
      }}
    >
      <div style={{ display: "flex", justifyContent: "center", gap: 8 }}>
        {props.questionCount ? props.questionCount : null}
        <CountdownTimer
          minutes={date.getMinutes()}
          seconds={date.getSeconds()}
          recording={recording}
          countdownFinished={countdownFinished}
          videoDuration={progress}
        />
      </div>

      <Spin tip={t("frontvint__uploading_video")} size="large" spinning={loading}>
        <VideoRecorder
          ref={recorder}
          demo={props.demo}
          time={props.time < 301 ? props.time : 10}
          onFinishRecording={onFinishRecording}
          onRecording={handleOnRecording}
          onCountdown={onCountdown}
          onRecordingProgress={onRecordingProgress}
          onRecorderReady={handleRecorderReady}
          onPermissionSuccess={onPermissionSuccess}
          onPermissionError={onPermissionError}
        />
      </Spin>

      <div style={{ display: "flex" }}>
        {recorderReady &&
        !recording &&
        !countdownFinished &&
        !file.url &&
        !props.flashInterview ? (
          <>
            <Button type="link" onClick={handleRecordStop}>
              {props.demo
                ? t("frontvint__button_test_record")
                : t("frontvint__button_record")}
            </Button>
            {props.demo && (
              <div
                style={{
                  flexGrow: 1,
                  display: "flex",
                  justifyContent: "end",
                  gap: 8,
                }}
              >
                <Button onClick={handleCancel}>
                  {t("frontvint__button_cancel")}
                </Button>
                <Button type="primary" onClick={handleStart}>
                  {t("frontvint__button_start")}
                </Button>
              </div>
            )}
          </>
        ) : null}

        {recording && countdownFinished && (
          <Button type="link" onClick={handleRecordStop}>
            <PauseOutlined /> {t("frontvint__button_stop")}
          </Button>
        )}

        {!recording && file.url && !loading && (
          <>
            {trialsRemaining > 0 && (
              <Button type="link" onClick={handleReRecord}>
                <RedoOutlined /> {t("frontvint__button_retry")}
              </Button>
            )}

            {props.demo ? (
              <div
                style={{
                  flexGrow: 1,
                  display: "flex",
                  justifyContent: "end",
                  gap: 8,
                }}
              >
                <Button onClick={handleCancel}>
                  {t("frontvint__button_cancel")}
                </Button>
                <Button type="primary" onClick={handleStart}>
                  {t("frontvint__button_start")}
                </Button>
              </div>
            ) : (
              <Button
                type="primary"
                onClick={handleUpload}
                style={{ marginLeft: "auto" }}
              >
                <UploadOutlined /> {t("frontvint__button_submit")}
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Recorder;
